import { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CheckBoxOutlined from "@mui/icons-material/CheckBoxOutlined";
import CheckBoxOutlineBlank from "@mui/icons-material/CheckBoxOutlineBlank";
import { useTheme } from "@mui/material/styles";
import { scheduleItemTypes } from "../../constants/program";
import { GeneralAPI } from "../../utils/generalApi";
import { capitalize, sanitizeHtml } from "../../utils/formatting";
import api from "../../utils/api";
import { apiUrls, urls } from "../../utils/urls";
import { Stack } from "@mui/system";
import { Button, ListItem, ListItemButton } from "@mui/material";
import useDisclosure from "../../hooks/ui/useDisclosure";
import LessonDialog from "../LessonDialog/LessonDialog";
import { ChevronRight, OpenInNew } from "@mui/icons-material";
import dayjs from "dayjs";
import { TESTING_APP_ORIGIN } from "../../../constants/misc";
import { useNavigate } from "react-router-dom";
import { testingTypes } from "../../constants/testing";

async function createDummyMetadata(scheduleItem) {
  const dummyMetadata = { ...scheduleItem.item_metadata };
  const itemType = scheduleItem.item_type;
  const subjectToChallengeCountMap = {
    english: 5,
    math: 3,
    reading: 4,
    science: 7,
  };
  const subjectToChallengeQuestionsToDoMap = {
    english: "1-15",
    math: "1-30",
    reading: "1-10",
    science: "1-6",
  };
  const dummyTest = {
    name: "16mc1",
    challenge_count: subjectToChallengeCountMap[dummyMetadata.subject],
    first_challenge: {
      uuid: "",
    },
    english_location: "Online",
    math_location: "Online",
    reading_location: "Online",
    science_location: "Online",
  };

  const dummyChallenge = {
    test: dummyTest,
    subject: dummyMetadata.subject,
    questions_to_do: subjectToChallengeQuestionsToDoMap[dummyMetadata.subject],
  };

  if (itemType == scheduleItemTypes.TEST) {
    let test;
    if (dummyMetadata.automatically_select_test.toString() == "true") {
      test = dummyTest;
    } else {
      const { data } = await new GeneralAPI("Test", {
        filter: {
          uuid: dummyMetadata.test.uuid,
        },
      }).send();
      test = data.data[0];
      test.first_challenge = { uuid: "" };
      test.challenge_count = subjectToChallengeCountMap[dummyMetadata.subject];
    }
    dummyMetadata.test = test;
  } else if (itemType == scheduleItemTypes.CHALLENGE) {
    let challenge;
    if (dummyMetadata.automatically_select_challenge.toString() == "true") {
      challenge = dummyChallenge;
    } else {
      const { data } = await new GeneralAPI("Challenge", {
        filter: {
          uuid: dummyMetadata.challenge.uuid,
        },
        keepRelated: true,
        exclude: ["test__answers", "test__conversion_chart"],
      }).send();
      challenge = data.data[0];
    }
    dummyMetadata.challenge = challenge;
  } else if (itemType == scheduleItemTypes.LESSON) {
    const { data } = await new GeneralAPI("Lesson", {
      filter: {
        uuid: dummyMetadata.lesson.uuid,
      },
    }).send();
    dummyMetadata.lesson = data.data[0];
  }

  return dummyMetadata;
}

function TestLocation({ test, subject }) {
  let testLocation =
    test.variant == "lesson"
      ? test.test_location
      : test[`${subject}_location`] || test?.test_location;
  if (testLocation?.startsWith("https")) {
    return (
      <Button
        component="a"
        href={testLocation}
        target="_blank"
        rel="noreferrer"
        variant="outlined"
        size="small"
        startIcon={<OpenInNew fontSize="small" />}
        onClick={(e) => e.stopPropagation()}
      >
        Open location
      </Button>
    );
  }
  return testLocation;
}

export function ScheduleListItem({
  scheduleItem,
  isPreview,
  isComplete: isCompleteProp,
  onSelectLesson,
  date,
  onCompletionStatusChange,
  employeeCoverageId,
  scheduleItemCompletionEndpoint,
  disabled = false,
  ...props
}) {
  const navigate = useNavigate();
  const [isComplete, setIsComplete] = useState(isCompleteProp);
  const [isLoading, setIsLoading] = useState(false);
  const [dummyMetadata, setDummyMetadata] = useState(null);
  const itemType = scheduleItem.item_type;
  const isBasic = itemType == scheduleItemTypes.BASIC;
  const theme = useTheme();

  const getMetadata = useCallback(() => {
    return isPreview ? dummyMetadata : scheduleItem.item_metadata;
  }, [isPreview, scheduleItem, dummyMetadata]);

  const handleCompletionChange = async () => {
    const newIsComplete = !isComplete;
    setIsComplete(newIsComplete);
    setIsLoading(true);
    const payload = {
      date: date.format("YYYY-MM-DD"),
      schedule_item_id: scheduleItem.uuid,
    };
    if (employeeCoverageId) {
      payload.employee_coverage_id = employeeCoverageId;
    }

    if (newIsComplete) {
      await api.post(scheduleItemCompletionEndpoint, payload);
    } else {
      await api.delete(scheduleItemCompletionEndpoint, {
        data: payload,
      });
    }
    if (onCompletionStatusChange) {
      onCompletionStatusChange();
    }
    setIsLoading(false);
  };

  const handleItemClick = () => {
    if (itemType == scheduleItemTypes.LESSON) {
      onSelectLesson(scheduleItem.item_metadata.lesson);
    } else if (itemType == scheduleItemTypes.MEMORIZATION) {
      // Build a lesson object from memorization metadata
      const memorizationLesson = {
        name: scheduleItem.label,
        embed_id: metadata?.memorization_info_embed_id,
        embed_link: metadata?.memorization_info_embed_link,
      };
      onSelectLesson(memorizationLesson);
    }
  };

  useEffect(() => {
    if (!isPreview) return;
    createDummyMetadata(scheduleItem).then(setDummyMetadata);
  }, [isPreview, scheduleItem]);

  useEffect(() => {
    setIsComplete(isCompleteProp);
  }, [isCompleteProp]);

  if (itemType === scheduleItemTypes.SECTION_LINK) {
    return null;
  }

  const metadata = getMetadata();

  const actionButtonIsLink =
    [
      scheduleItemTypes.CHALLENGE,
      scheduleItemTypes.TEST,
      scheduleItemTypes.LINK,
    ].includes(itemType) ||
    (itemType == scheduleItemTypes.LESSON &&
      !scheduleItem.item_metadata.lesson?.can_embed);

  let actionButtonLink;
  let newTab = false;
  if (itemType === scheduleItemTypes.TEST) {
    const test = scheduleItem.item_metadata.test;
    const subject = scheduleItem.item_metadata.subject;
    const addParam = (key, value) =>
      (link +=
        (link.endsWith("?") ? "" : "&") +
        key +
        "=" +
        encodeURIComponent(value));
    let link = `${TESTING_APP_ORIGIN}${urls.testing.home.takeTest()}?`;
    let testingType =
      test?.variant === "lesson"
        ? testingTypes.LESSON_TEST
        : subject
          ? testingTypes.SUBJECT_TEST
          : testingTypes.FULL_TEST;
    if (
      scheduleItem.item_metadata.take_test_in_challenges &&
      test?.first_challenge
    ) {
      link += "test_id=" + test.first_challenge.uuid;
    } else if (test) {
      link += "test_id=" + test.uuid;
      if (test.variant !== "lesson" && subject) {
        addParam("subject", subject);
      }
    } else if (subject) {
      addParam("subject", subject);
    }
    addParam("testing_type", testingType);
    actionButtonLink = link;
  } else if (itemType == scheduleItemTypes.LESSON && actionButtonIsLink) {
    actionButtonLink = scheduleItem.item_metadata.lesson.link;
  } else if (itemType == scheduleItemTypes.CHALLENGE) {
    const challenge = scheduleItem.item_metadata.challenge;
    const subject = scheduleItem.item_metadata.subject;
    const addParam = (key, value) =>
      (testingAppLink +=
        (testingAppLink.endsWith("?") ? "" : "&") +
        key +
        "=" +
        encodeURIComponent(value));
    let testingAppLink = `${TESTING_APP_ORIGIN}${urls.testing.home.takeTest()}?`;
    if (challenge) {
      testingAppLink += "challenge_id=" + challenge.uuid;
    } else if (subject) {
      addParam("subject", subject);
    }
    addParam("testing_type", testingTypes.CHALLENGE);
    actionButtonLink = testingAppLink;
  } else if (itemType == scheduleItemTypes.LINK) {
    actionButtonLink = scheduleItem.item_metadata.url;
    newTab = scheduleItem.item_metadata.new_tab || false;
  }

  const isSameOrigin =
    typeof window !== "undefined" &&
    window.location.origin === TESTING_APP_ORIGIN;
  const useNavigateForLink =
    isSameOrigin &&
    actionButtonIsLink &&
    actionButtonLink?.startsWith(TESTING_APP_ORIGIN);
  const pathAndSearch = useNavigateForLink
    ? actionButtonLink.slice(TESTING_APP_ORIGIN.length)
    : "";

  const completeProps = isBasic
    ? {
        ...props,
        sx: {
          pl: 0.5,
        },
      }
    : {
        ...props,
        href: useNavigateForLink
          ? undefined
          : actionButtonIsLink
            ? actionButtonLink
            : undefined,
        target: useNavigateForLink ? undefined : newTab ? "_blank" : undefined,
        onClick: actionButtonIsLink
          ? useNavigateForLink
            ? (e) => {
                e.preventDefault();
                navigate(pathAndSearch);
              }
            : undefined
          : handleItemClick,
      };
  const ListItemComponent = isBasic ? ListItem : ListItemButton;
  return (
    <ListItemComponent {...completeProps}>
      <ListItemIcon
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          if (disabled || isLoading) {
            return;
          }
          handleCompletionChange(e);
        }}
        disabled={isLoading || disabled}
      >
        <IconButton disabled={isLoading || disabled}>
          {isComplete ? <CheckBoxOutlined /> : <CheckBoxOutlineBlank />}
        </IconButton>
      </ListItemIcon>

      <ListItemText
        disableTypography
        primary={
          <Stack direction={"row"} spacing={1} alignItems={"center"}>
            <Box>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: "bold",
                  color: theme.palette.text.primary,
                  textDecoration: isComplete ? "line-through" : "none",
                }}
              >
                {scheduleItem.required && (
                  <Box
                    component="span"
                    sx={{
                      color: theme.palette.error.main,
                      mr: "0.25rem",
                    }}
                  >
                    *
                  </Box>
                )}
                {scheduleItem.label}
              </Typography>
            </Box>
            {itemType != scheduleItemTypes.BASIC ? (
              actionButtonIsLink ? (
                <OpenInNew fontSize="small" color="secondary" />
              ) : (
                <ChevronRight fontSize="small" color="secondary" />
              )
            ) : (
              <></>
            )}
          </Stack>
        }
        secondary={
          <>
            <Box
              sx={{
                ml: ".5rem",
                opacity: isComplete ? ".5" : "1",
                whiteSpace: "normal",
                overflowWrap: "break-word",
                wordBreak: "break-word",
                pointerEvents: isComplete ? "none" : undefined,
              }}
            >
              {sanitizeHtml(scheduleItem.description)}

              {metadata?.test && itemType === scheduleItemTypes.TEST && (
                <Box mt={1}>
                  <Typography variant="body1" fontWeight="bold">
                    Test Info
                  </Typography>
                  <Box sx={{ ml: "1rem" }}>
                    <Box m={0}>
                      <Typography
                        variant="overline"
                        fontWeight="bold"
                        component="span"
                      >
                        Test:
                      </Typography>{" "}
                      <Typography variant="subtitle1" component="span">
                        {metadata.test.visible_name || metadata.test.name}{" "}
                        {capitalize(metadata.subject)}
                      </Typography>
                    </Box>
                    <Box m={0}>
                      <Typography
                        variant="overline"
                        fontWeight="bold"
                        component="span"
                      >
                        Location:
                      </Typography>{" "}
                      <Typography variant="subtitle1" component="span">
                        <TestLocation
                          test={metadata.test}
                          subject={metadata.subject}
                        />
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              {metadata?.challenge &&
                itemType === scheduleItemTypes.CHALLENGE && (
                  <Box mt={1}>
                    <Typography variant="body1" fontWeight="bold">
                      Challenge Info
                    </Typography>
                    <Box sx={{ ml: "1rem" }}>
                      <Box m={0}>
                        <Typography
                          variant="overline"
                          fontWeight="bold"
                          component="span"
                        >
                          Challenge:
                        </Typography>{" "}
                        <Typography variant="subtitle1" component="span">
                          {metadata.challenge?.test?.visible_name ||
                            metadata.challenge?.test?.name}{" "}
                          {capitalize(metadata.subject)} Questions{" "}
                          {metadata.challenge.first_question} -{" "}
                          {metadata.challenge.last_question}
                        </Typography>
                      </Box>
                      <Box m={0}>
                        <Typography
                          variant="overline"
                          fontWeight="bold"
                          component="span"
                        >
                          Location:
                        </Typography>{" "}
                        <Typography variant="subtitle1" component="span">
                          <TestLocation
                            test={metadata.challenge.test}
                            subject={metadata.challenge.subject}
                          />
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}
            </Box>
          </>
        }
      />
    </ListItemComponent>
  );
}

export default function ScheduleItemList({
  scheduleItems,
  isPreview,
  completedItemsMap = {},
  onCompletionStatusChange,
  employeeCoverageId,
  scheduleItemCompletionEndpoint = apiUrls.scheduling.scheduleItemCompletion(),
  date,
  maxHeight,
  readOnly = false,
}) {
  const theme = useTheme();
  const documentDisc = useDisclosure();
  const [selectedLesson, setSelectedLesson] = useState(null);
  return (
    <Box
      sx={{
        padding: "1rem",
        backgroundColor: theme.palette.background.default,
        borderRadius: ".5rem",
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <List
        sx={{
          ...(maxHeight && {
            maxHeight:
              typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight,
            overflowY: "auto",
            overflowX: "hidden",
            // Smooth scrolling
            scrollBehavior: "smooth",
            // Custom scrollbar styling for webkit browsers
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.action.hover,
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.action.disabled,
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: theme.palette.action.disabledBackground,
              },
            },
            // Firefox scrollbar styling
            scrollbarWidth: "thin",
            scrollbarColor: `${theme.palette.action.disabled} ${theme.palette.action.hover}`,
          }),
        }}
      >
        {scheduleItems.map((scheduleItem, i) => (
          <ScheduleListItem
            scheduleItem={scheduleItem}
            isComplete={!!completedItemsMap[scheduleItem.uuid]}
            key={i}
            sx={{
              padding: ".25rem",
            }}
            divider={i != scheduleItems.length - 1}
            isPreview={isPreview}
            date={date}
            onSelectLesson={(lesson) => {
              setSelectedLesson(lesson);
              documentDisc.onOpen();
            }}
            employeeCoverageId={employeeCoverageId}
            onCompletionStatusChange={onCompletionStatusChange}
            disabled={
              readOnly ||
              (completedItemsMap[scheduleItem.uuid]
                ? dayjs().diff(
                    dayjs(completedItemsMap[scheduleItem.uuid]),
                    "days",
                  ) >= 1
                : false)
            }
            scheduleItemCompletionEndpoint={scheduleItemCompletionEndpoint}
          />
        ))}
      </List>
      <LessonDialog
        lesson={selectedLesson}
        {...documentDisc.getDisclosureProps()}
      />
    </Box>
  );
}
